import Typography from '@mui/material/Typography'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { Grid } from '@mui/material'
import { pageShouldShowHeroSignUpButton } from '@app/utils/pageUtils'
import { TabsEnum } from '@app/utils/enums'
import { signUpEnabled } from '@app/utils/signUpUtils'
import { DateTime } from 'luxon'
import { RootState } from '@app/store'
import SignUpOrCancelButton from './SignUpOrCancelButton'
import { rem } from '@app/theme/materialUITheme'
import SignUpText from './SignUpText'
import { useCallback } from 'react'

type Props = {
  onlyBtn?: boolean
}

const SignUp = ({ onlyBtn = false }: Props) => {
  const site = useSelector((store: StoreState) => store.tournamentReducer.tournamentSite)
  const currentPage: TabsEnum | null = useSelector(
    (store: RootState) => store.pageReducer.currentPage || null,
  ) as TabsEnum

  const courseTimezone = useSelector(
    (store: RootState) => store.tournamentReducer.tournamentSite.tournament.rounds[0].club.timeZone,
  )

  if (!site || !site.onlineEntriesEnabled) {
    return null
  }

  const entryOpenDateTime = DateTime.fromISO(site.registrationStartsTime).setZone(courseTimezone)
  const entryEndDateTime = DateTime.fromISO(site.registrationEndsTime).setZone(courseTimezone)
  const cancellationsOpenDateTime = DateTime.fromISO(site.registrationCancellingStartsTime).setZone(courseTimezone)
  const cancelByDateTime = DateTime.fromISO(site.registrationCancellingEndsTime).setZone(courseTimezone)
  const entriesSetButNotOpen = DateTime.now() < entryOpenDateTime

  const cancellationsClosed = DateTime.now() > cancelByDateTime
  const cancellationsWillClose = DateTime.now() < cancelByDateTime && DateTime.now() > entryEndDateTime
  const entriesAndCancellationsOpen =
    site.registerAndCancelTimeFramesEnabled && DateTime.now() >= entryOpenDateTime && DateTime.now() <= cancelByDateTime

  const isEntriesOpen = DateTime.now() >= entryOpenDateTime && DateTime.now() <= entryEndDateTime
  const isCancellationsOpen = DateTime.now() >= cancellationsOpenDateTime && DateTime.now() <= cancelByDateTime
  const noLongerAcceptingEntries = DateTime.now() > entryEndDateTime

  const signUpButtons = useCallback(
    (location: 'footer' | 'header') => {
      const signUpButton = (
        <Grid item>
          <SignUpOrCancelButton
            btnText={
              location === 'header' && site.registerAndCancelTimeFramesEnabled && noLongerAcceptingEntries ? (
                <FormattedMessage id={'strings.entriesClosedBtn'} />
              ) : (
                ''
              )
            }
            disabled={site.registerAndCancelTimeFramesEnabled && !isEntriesOpen}
          />
        </Grid>
      )

      return site.onlineEntriesEnabled &&
        (!site.registerAndCancelTimeFramesEnabled ||
          (site.registerAndCancelTimeFramesEnabled && !noLongerAcceptingEntries) ||
          (site.registerAndCancelTimeFramesEnabled && noLongerAcceptingEntries && isCancellationsOpen)) ? (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ display: 'flex', flexDirection: 'column', gap: rem(12), width: 1 }}
        >
          {location === 'header' &&
          ((site.registerAndCancelTimeFramesEnabled && !entriesSetButNotOpen) ||
            !site.registerAndCancelTimeFramesEnabled)
            ? signUpButton
            : location === 'footer' &&
              ((site.registerAndCancelTimeFramesEnabled && isEntriesOpen) ||
                !site.registerAndCancelTimeFramesEnabled) &&
              signUpButton}

          {(!site.registerAndCancelTimeFramesEnabled ||
            (site.registerAndCancelTimeFramesEnabled && (!entriesSetButNotOpen || isCancellationsOpen))) && (
            <Grid item>
              <SignUpOrCancelButton
                color={'secondary'}
                disabled={
                  site.registerAndCancelTimeFramesEnabled &&
                  (cancellationsClosed || DateTime.now() < cancellationsOpenDateTime)
                }
              />
            </Grid>
          )}
        </Grid>
      ) : null
    },
    [
      entryOpenDateTime,
      entryEndDateTime,
      cancelByDateTime,
      site,
      noLongerAcceptingEntries,
      cancellationsClosed,
      cancellationsOpenDateTime,
    ],
  )

  if (onlyBtn) {
    const showButton = pageShouldShowHeroSignUpButton(currentPage) || signUpEnabled(site)
    if (!showButton) {
      return null
    }
    return signUpButtons('header')
  }

  return (
    <Grid container direction="column" justifyContent="center" alignItems="center" gap={rem(20)}>
      <Grid item>
        <Typography variant="h2">
          <FormattedMessage id={'strings.signUpHeader'} />
        </Typography>
      </Grid>

      <SignUpText
        noLongerAcceptingEntries={noLongerAcceptingEntries}
        cancellationsClosed={cancellationsClosed}
        cancellationsWillClose={cancellationsWillClose}
        entriesSetButNotOpen={entriesSetButNotOpen}
        entriesAndCancellationsOpen={entriesAndCancellationsOpen}
        entryOpenDateTime={entryOpenDateTime}
        entryEndDateTime={entryEndDateTime}
        cancelByDateTime={cancelByDateTime}
      />

      {signUpButtons('footer')}
    </Grid>
  )
}

export default SignUp
